import { useState } from "react";
import { BugReportMicButton } from "@/components/common/BugReportMicButton";
import { devNotesAssetDelete, devNotesReview } from "@/lib/devActionableNotesClient";
import type { DevActionableNote } from "./devActionableNoteTypes";
import { noteAssetIncludeInSync, noteHasAsset } from "./devActionableNoteTypes";
import {
  DevPanelActionableNoteBodyPreview,
  DevPanelActionableNoteHeader,
  DevPanelActionableNoteMeta,
} from "./DevPanelActionableNoteCard";

type Props = {
  notes: DevActionableNote[];
  getToken: () => Promise<string | null>;
  savingId: string | null;
  setSavingId: (id: string | null) => void;
  onError: (msg: string) => void;
  onDone: () => void;
};

function appendDictation(current: string, chunk: string): string {
  const trimmed = chunk.trim();
  if (!trimmed) return current;
  return current ? current.replace(/\s+$/, "") + " " + trimmed : trimmed;
}

export function DevPanelActionableNotesReview({ notes, getToken, savingId, setSavingId, onError, onDone }: Props) {
  const [alterOpenId, setAlterOpenId] = useState<string | null>(null);
  const [alterBody, setAlterBody] = useState<Record<string, string>>({});
  const [reviewerNote, setReviewerNote] = useState<Record<string, string>>({});
  const [includeAsset, setIncludeAsset] = useState<Record<string, boolean>>({});
  const [deletingAssetId, setDeletingAssetId] = useState<string | null>(null);

  function includeForNote(note: DevActionableNote): boolean {
    if (includeAsset[note.id] !== undefined) return includeAsset[note.id]!;
    return noteAssetIncludeInSync(note);
  }

  async function deleteAsset(note: DevActionableNote) {
    const token = await getToken();
    if (!token) return;
    setDeletingAssetId(note.id);
    const result = await devNotesAssetDelete(token, { id: note.id });
    setDeletingAssetId(null);
    if (!result.ok) {
      onError(result.error);
      return;
    }
    onDone();
  }

  async function review(
    note: DevActionableNote,
    action: "approve" | "alter" | "deny",
    effective_body?: string,
  ) {
    if (action === "deny" && !window.confirm("Deny and delete this submission?")) return;
    const token = await getToken();
    if (!token) return;
    setSavingId(note.id);
    const payload: Parameters<typeof devNotesReview>[1] = {
      id: note.id,
      action,
      effective_body,
      reviewer_note: reviewerNote[note.id]?.trim() || undefined,
    };
    if (noteHasAsset(note)) {
      payload.include_asset_in_sync = includeForNote(note);
    }
    const result = await devNotesReview(token, payload);
    setSavingId(null);
    if (!result.ok) {
      onError(result.error);
      return;
    }
    setAlterOpenId(null);
    onDone();
  }

  if (!notes.length) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-amber-950">Pending review</h3>
      <ul className="space-y-3">
        {notes.map((note) => {
          const altering = alterOpenId === note.id;
          const assetInteractive = noteHasAsset(note)
            ? {
                interactive: true as const,
                includeInSync: includeForNote(note),
                onIncludeInSyncChange: (next: boolean) =>
                  setIncludeAsset((p) => ({ ...p, [note.id]: next })),
                onDelete: () => void deleteAsset(note),
                deleting: deletingAssetId === note.id,
                disabled: savingId === note.id,
              }
            : undefined;

          return (
            <li key={note.id} className="rounded-xl border border-amber-200/80 bg-white p-3 space-y-2 dark:bg-zinc-900">
              <DevPanelActionableNoteHeader note={note} />
              <DevPanelActionableNoteBodyPreview note={note} />
              <DevPanelActionableNoteMeta note={note} assetInteractive={assetInteractive} />
              {altering ? (
                <div className="space-y-2 rounded-lg border border-amber-100 bg-amber-50/50 p-2">
                  <p className="text-[10px] font-medium uppercase text-amber-900">Corrected prompt for sync</p>
                  <textarea
                    className="w-full rounded border border-amber-200 px-2 py-1.5 text-sm"
                    rows={4}
                    value={alterBody[note.id] ?? note.body}
                    onChange={(e) => setAlterBody((p) => ({ ...p, [note.id]: e.target.value }))}
                  />
                  <div className="flex justify-end">
                    <BugReportMicButton
                      disabled={savingId === note.id || deletingAssetId === note.id}
                      onAppend={(chunk) =>
                        setAlterBody((p) => ({
                          ...p,
                          [note.id]: appendDictation(p[note.id] ?? note.body, chunk),
                        }))
                      }
                    />
                  </div>
                  <input
                    className="w-full rounded border border-amber-200 px-2 py-1 text-xs"
                    placeholder="Optional reviewer note"
                    value={reviewerNote[note.id] ?? ""}
                    onChange={(e) => setReviewerNote((p) => ({ ...p, [note.id]: e.target.value }))}
                  />
                  <div className="flex justify-end">
                    <BugReportMicButton
                      disabled={savingId === note.id || deletingAssetId === note.id}
                      onAppend={(chunk) =>
                        setReviewerNote((p) => ({
                          ...p,
                          [note.id]: appendDictation(p[note.id] ?? "", chunk),
                        }))
                      }
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded-lg bg-amber-800 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
                      disabled={savingId === note.id || deletingAssetId === note.id}
                      onClick={() =>
                        void review(note, "alter", (alterBody[note.id] ?? note.body).trim())
                      }
                    >
                      Save & approve altered
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-gray-300 px-3 py-1 text-xs"
                      onClick={() => setAlterOpenId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="rounded-lg bg-emerald-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
                    disabled={savingId === note.id}
                    onClick={() => void review(note, "approve")}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="rounded-lg border border-amber-400 bg-white px-3 py-1 text-xs font-semibold text-amber-900 disabled:opacity-50"
                    disabled={savingId === note.id}
                    onClick={() => {
                      setAlterOpenId(note.id);
                      setAlterBody((p) => ({ ...p, [note.id]: p[note.id] ?? note.body }));
                      setIncludeAsset((p) => ({
                        ...p,
                        [note.id]: p[note.id] ?? noteAssetIncludeInSync(note),
                      }));
                    }}
                  >
                    Alter
                  </button>
                  <button
                    type="button"
                    className="rounded-lg border border-red-300 px-3 py-1 text-xs font-semibold text-red-800 disabled:opacity-50"
                    disabled={savingId === note.id}
                    onClick={() => void review(note, "deny")}
                  >
                    Deny
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
